/**
 * "What could go wrong" — the risks of one strategy, on its own surface.
 *
 * Opened from the card's button (owner, 2 Oct 2026). It reads the single risk list in
 * src/strategy/strategyRisks.ts, the same list the Statement of Advice prints, so the screen and the
 * document cannot disagree. Nothing is recorded about it being opened.
 *
 * THE LAYOUT IS THE POINT. The owner's brief: if it looks like a wall of bland text the client won't
 * focus. So: what is about THIS client comes first, in amber, because it is the part they cannot get
 * anywhere else; then one block per part of the plan, each point a bold claim with its own icon and
 * one sentence of why. Short lines, generous space, no paragraphs.
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    X, Lock, Receipt, Home, Landmark, TrendingDown, Clock, Heart, Percent, Users, FileSignature,
    Repeat, Coins, ShieldCheck, CalendarDays, Wrench, AlertTriangle, type LucideIcon,
} from 'lucide-react';
import { GradientText, Button } from '../ui/index.tsx';
import type { RiskIcon, RiskPoint, StrategyRisks } from '../../strategy/strategyRisks.ts';

const ICONS: Record<RiskIcon, LucideIcon> = {
    lock: Lock, receipt: Receipt, home: Home, landmark: Landmark, trendDown: TrendingDown, clock: Clock,
    heart: Heart, percent: Percent, users: Users, fileSignature: FileSignature, repeat: Repeat, coins: Coins,
    shield: ShieldCheck, calendar: CalendarDays, wrench: Wrench, alert: AlertTriangle,
};

const Point = ({ point, tone }: { point: RiskPoint; tone: 'amber' | 'slate' }) => {
    const Icon = ICONS[point.icon];
    return (
        <li className="flex gap-3">
            <span
                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    tone === 'amber' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700'
                }`}
            >
                <Icon size={15} aria-hidden />
            </span>
            <p className="text-sm leading-relaxed text-slate-700">
                <span className="font-semibold text-slate-900">{point.lead}</span> {point.body}
            </p>
        </li>
    );
};

export interface StrategyRisksPanelProps {
    strategyName: string;
    risks: StrategyRisks;
    onClose: () => void;
    /** "your" for a client reading about themselves; a first name for an adviser's view. */
    possessive?: string;
}

/** The box itself, without the overlay: what the client reads. */
export const StrategyRisksSheet = ({ strategyName, risks, onClose, possessive = 'your', closeRef }: StrategyRisksPanelProps & { closeRef?: React.Ref<HTMLButtonElement> }) => {
    const total = risks.forYou.length + risks.sections.reduce((n, s) => n + s.points.length, 0);
    const you = possessive === 'your' ? 'you' : possessive;
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="strategy-risks-heading"
            className="flex w-full max-w-2xl flex-col rounded-2xl border border-slate-200/80 bg-white shadow-2xl max-h-[calc(100vh-4rem)]"
        >
            <div className="flex items-start gap-4 border-b-2 border-amber-400/80 px-6 pb-4 pt-5">
                <div className="min-w-0">
                    <h2 id="strategy-risks-heading" className="text-2xl font-bold tracking-tight">
                        <GradientText>What could go wrong</GradientText>
                    </h2>
                    <p className="mt-0.5 text-sm text-slate-500">{strategyName}</p>
                </div>
                <button
                    type="button"
                    ref={closeRef}
                    onClick={onClose}
                    aria-label="Close"
                    className="ml-auto shrink-0 rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                >
                    <X size={20} aria-hidden />
                </button>
            </div>

            <div className="overflow-y-auto px-6 pb-6 pt-5">
                <p className="mb-5 text-sm leading-relaxed text-slate-600">
                    Every plan has trade-offs. These are the {total} that matter for this one, so {you} can choose with eyes open.
                    The same points are set out in the Statement of Advice.
                </p>

                {risks.forYou.length > 0 && (
                    <section className="mb-5 rounded-xl border border-amber-200 p-4">
                        <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.08em] text-amber-800">
                            <AlertTriangle size={15} aria-hidden /> For {you} specifically
                        </h3>
                        <ul className="space-y-3.5">
                            {risks.forYou.map((p) => (
                                <Point key={p.lead} point={p} tone="amber" />
                            ))}
                        </ul>
                    </section>
                )}

                <div className="space-y-4">
                    {risks.sections.map((section) => (
                        <section key={section.kind} className="rounded-xl border border-slate-200 p-4">
                            <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.08em] text-slate-500">{section.title}</h3>
                            <ul className="space-y-3.5">
                                {section.points.map((p) => (
                                    <Point key={p.lead} point={p} tone="slate" />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>

                <div className="mt-6 flex items-center justify-between gap-4 border-t border-slate-200 pt-4">
                    <p className="text-xs leading-relaxed text-slate-500">
                        None of this means the plan is wrong. It means {you} should be comfortable with each point before choosing it.
                    </p>
                    <Button onClick={onClose} variant="primary" size="sm" className="shrink-0">
                        Got it
                    </Button>
                </div>
            </div>
        </div>
    );
};

const StrategyRisksPanel = (props: StrategyRisksPanelProps) => {
    const { onClose } = props;
    const closeRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        closeRef.current?.focus();
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/55 p-4 sm:p-8"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <StrategyRisksSheet {...props} closeRef={closeRef} />
        </div>,
        document.body,
    );
};

export default StrategyRisksPanel;
